import { Controller } from "@hotwired/stimulus"

// A chat room page (chat_rooms/show). Messages are rendered the same for
// everyone (live ones come from a broadcast with no viewer), so this marks
// the viewer's own messages, shows the Responder/Eliminar buttons they may
// use, keeps the newest message in view and tells the server what was read.
export default class extends Controller {
  static targets = ["thread", "messages", "message", "form", "input", "replyField", "replyPreview", "replyText"]
  static values = {
    currentUserId: Number,
    canModerate: Boolean,
    canReply: Boolean,
    deleteWindow: Number, // seconds an author may delete their own message
    readUrl: String
  }

  connect() {
    this.ready = true
    this.fitToScreen = this.fitToScreen.bind(this)
    window.addEventListener("resize", this.fitToScreen)
    this.fitToScreen()
    this.scrollToEnd()
  }

  disconnect() {
    clearTimeout(this.readTimer)
    window.removeEventListener("resize", this.fitToScreen)
  }

  // The thread takes the height left on screen between its top and the
  // composer, the phone tab bar and a small gap, so the whole window fits
  // without page scrolling (CSS only gives a starting size).
  fitToScreen() {
    const thread = this.threadTarget
    const atEnd = this.nearEnd()
    const nav = document.querySelector(".bottom-nav")
    const navHeight = nav && getComputedStyle(nav).display !== "none" ? nav.offsetHeight : 0
    const below = this.element.getBoundingClientRect().bottom - thread.getBoundingClientRect().bottom
    const top = thread.getBoundingClientRect().top + window.scrollY
    const height = window.innerHeight - top - below - navHeight - 16
    thread.style.height = `${Math.max(height, 224)}px`
    if (atEnd) this.scrollToEnd()
  }

  messageTargetConnected(message) {
    const mine = Number(message.dataset.authorId) === this.currentUserIdValue
    if (mine) {
      message.classList.replace("chat-start", "chat-end")
      message.querySelector(".chat-bubble:not(.chat-bubble-deleted)")?.classList.add("chat-bubble-primary")
    }
    this.showActions(message, mine)

    // Live arrival (initial messages connect before connect()).
    if (!this.ready) return
    this.keepInOrder(message)
    if (mine || this.nearEnd()) this.scrollToEnd()
    if (!mine) this.scheduleRead()
  }

  // Broadcasts and the sender's own response can arrive in any order (an @ia
  // answer may beat its question): keep messages sorted by id.
  keepInOrder(message) {
    const id = this.messageId(message)
    const later = this.messageTargets.find((other) => other !== message && this.messageId(other) > id)
    if (later && message.compareDocumentPosition(later) & Node.DOCUMENT_POSITION_PRECEDING) {
      later.before(message)
    }
  }

  messageId(message) {
    return Number(message.id.replace("chat_message_", ""))
  }

  showActions(message, mine) {
    const reply = message.querySelector("[data-role=reply]")
    if (reply && this.canReplyValue) reply.classList.remove("hidden")

    const remove = message.querySelector("[data-role=delete]")
    if (!remove) return
    if (this.canModerateValue) {
      remove.classList.remove("hidden")
    } else if (mine) {
      const left = Number(message.dataset.createdAt) + this.deleteWindowValue - Date.now() / 1000
      if (left > 0) {
        remove.classList.remove("hidden")
        setTimeout(() => remove.classList.add("hidden"), left * 1000)
      }
    }
  }

  reply({ params: { id, author, excerpt } }) {
    if (!this.hasFormTarget) return
    this.replyFieldTarget.value = id
    this.replyTextTarget.textContent = `Respondiendo a ${author}: ${excerpt}`
    this.replyPreviewTarget.classList.remove("hidden")
    this.inputTarget.focus()
  }

  cancelReply() {
    if (!this.hasFormTarget) return
    this.replyFieldTarget.value = ""
    this.replyPreviewTarget.classList.add("hidden")
  }

  // Enter sends, Shift+Enter is a new line. On touch screens Enter is a new
  // line too (the keyboard has no Shift); the Enviar button sends.
  keydown(event) {
    if (event.key !== "Enter" || event.shiftKey || event.isComposing) return
    if (window.matchMedia("(pointer: coarse)").matches) return
    event.preventDefault()
    if (this.inputTarget.value.trim()) this.formTarget.requestSubmit()
  }

  resize() {
    const input = this.inputTarget
    input.style.height = "auto"
    input.style.height = `${Math.min(input.scrollHeight, 200)}px`
    this.fitToScreen()
  }

  sent(event) {
    if (!event.detail.success) return
    this.formTarget.reset()
    this.cancelReply()
    this.resize()
    this.inputTarget.focus()
  }

  // The thread scrolls inside the chat window.
  nearEnd() {
    const thread = this.threadTarget
    return thread.scrollTop + thread.clientHeight >= thread.scrollHeight - 200
  }

  scrollToEnd() {
    this.threadTarget.scrollTop = this.threadTarget.scrollHeight
  }

  scheduleRead() {
    clearTimeout(this.readTimer)
    this.readTimer = setTimeout(() => this.markRead(), 1000)
  }

  markRead() {
    const last = this.messageTargets.at(-1)
    if (!last || document.hidden) return
    const token = document.querySelector("meta[name=csrf-token]")?.content
    fetch(this.readUrlValue, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
      body: JSON.stringify({ last_message_id: this.messageId(last) })
    })
  }
}
